import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { http, slugify } from "../../lib/api";
import { useStore } from "../../context/StoreContext";

const Suggestion = ({ d, active, onPick }) => (
  <li>
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onPick(d)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? "bg-sand" : "hover:bg-sand"}`}
      data-testid={`destination-suggestion-${d.slug}`}
    >
      {d.image ? (
        <img src={d.image} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
      ) : (
        <span className="w-11 h-11 rounded-lg ocean-gradient grid place-items-center shrink-0">
          <MapPin className="w-5 h-5 text-white" />
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-navy truncate">{d.name}</span>
        <span className="block text-xs text-navy/60 truncate">
          {d.region}
          {d.country && d.country !== d.region ? ` · ${d.country}` : ""}
        </span>
      </span>
      <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 shrink-0 ${d.product_count ? "bg-turq/15 text-[#0078a0]" : "bg-navy/5 text-navy/50"}`}>
        {d.product_count ? `${d.product_count} design${d.product_count > 1 ? "s" : ""}` : "Coming soon"}
      </span>
    </button>
  </li>
);

export const DestinationSearch = ({ variant = "hero", autoFocus = false, onDone }) => {
  const { preview } = useStore();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [selected, setSelected] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      http
        .get("/destinations/search", { params: { q, include_drafts: preview, limit: 7 } })
        .then((r) => {
          setItems(r.data);
          setActive(-1);
        })
        .catch(() => setItems([]));
    }, 120);
    return () => clearTimeout(t);
  }, [q, preview]);

  useEffect(() => {
    const h = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (slug) => {
    setOpen(false);
    onDone?.();
    navigate(`/destinations/${slug}`);
  };

  const pick = (d) => {
    setSelected(d);
    setQ(d.name);
    setOpen(false);
  };

  const explore = async () => {
    if (selected && selected.name === q) return go(selected.slug);
    if (!q.trim()) return setOpen(true);
    const r = await http.get("/destinations/search", { params: { q, limit: 1 } }).catch(() => ({ data: [] }));
    go(r.data[0]?.slug || slugify(q));
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && active >= 0 && items[active]) pick(items[active]);
      else explore();
    } else if (e.key === "Escape") setOpen(false);
  };

  const hero = variant === "hero";
  return (
    <div ref={boxRef} className="relative w-full" data-testid={`destination-search-${variant}`}>
      <div
        className={`flex items-center bg-white rounded-full shadow-[0_20px_60px_-15px_rgba(6,27,50,0.55)] border-2 border-transparent focus-within:border-sun transition-colors ${
          hero ? "p-1.5 sm:p-2 pl-3 sm:pl-6" : "p-1.5 pl-4 mt-4"
        }`}
      >
        <MapPin className={`shrink-0 text-navy ${hero ? "w-6 h-6" : "w-5 h-5"}`} aria-hidden />
        <input
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQ(e.target.value);
            setSelected(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="✈️ Select your destination..."
          aria-label="Search your travel destination"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className={`flex-1 min-w-0 bg-transparent outline-none text-navy placeholder:text-navy/50 px-2 sm:px-3 ${hero ? "text-base sm:text-lg py-3" : "text-base py-2.5"}`}
          data-testid="destination-search-input"
        />
        <button
          type="button"
          onClick={explore}
          className={`shrink-0 rounded-full bg-sun text-navy font-extrabold tracking-wide flex items-center gap-2 shadow-md hover:bg-[#ffe34d] hover:shadow-lg active:scale-95 transition-[background-color,box-shadow,transform] ${
            hero ? "px-4 sm:px-9 py-4 text-base sm:text-xl" : "px-3.5 sm:px-5 py-3 text-base"
          }`}
          aria-label="Explore destination"
          data-testid="destination-explore-button"
        >
          <span className="hidden sm:inline">EXPLORE</span> <ArrowRight className="w-5 h-5" strokeWidth={3} />
        </button>
      </div>
      {open && items.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl p-2 max-h-[340px] overflow-y-auto animate-in fade-in slide-in-from-top-2 text-left"
          data-testid="destination-suggestions"
        >
          {!q && <li className="px-3 pt-1 pb-2 text-[11px] font-bold uppercase tracking-widest text-navy/40">Popular destinations</li>}
          {items.map((d, i) => (
            <Suggestion key={d.slug} d={d} active={i === active} onPick={pick} />
          ))}
        </ul>
      )}
      {open && q.length > 1 && items.length === 0 && (
        <div className="absolute z-30 left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl p-4 text-sm text-navy/70 text-left" data-testid="destination-no-suggestions">
          No exact match yet — press <b>Explore</b> and we'll suggest similar destinations.
        </div>
      )}
    </div>
  );
};
